import { Component, type ErrorInfo, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'

import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { AlertTriangle } from 'lucide-react'

interface RootErrorBoundaryProps {
  children: ReactNode
}

interface RootErrorBoundaryState {
  error: Error | null
}

/**
 * Top-level error boundary that catches anything thrown ABOVE the
 * TanStack Router (e.g. inside the AuthProvider, the QueryClientProvider,
 * or the router itself before any route component mounts).
 *
 * The TanStack Router has its own `errorComponent` (see __root.tsx) that
 * handles errors thrown WITHIN route components and gives the user a
 * full-fidelity "Try again" button. This boundary is the last line of
 * defense: it survives even a broken router and offers a hard reload as
 * the only recovery path.
 *
 * The boundary forwards every caught error to ``Sentry.captureException``.
 * If Sentry isn't initialized (no VITE_SENTRY_DSN), that call is a no-op,
 * so the boundary works identically in development.
 */
export class RootErrorBoundary extends Component<
  RootErrorBoundaryProps,
  RootErrorBoundaryState
> {
  override state: RootErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): RootErrorBoundaryState {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Sentry's React integration would auto-capture this via
    // ``Sentry.ErrorBoundary``, but we want full control over the fallback
    // UI (the bundled ``Sentry.ErrorBoundary`` ships a `<p>` for a fallback
    // and the upgrade path to a custom component is awkward). Capture
    // manually so events still flow when a DSN is configured.
    Sentry.captureException(error, { contexts: { react: { componentStack: info.componentStack ?? undefined } } })
  }

  private handleReload = (): void => {
    if (typeof window !== 'undefined') {
      window.location.reload()
    }
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children

    // Raw exception messages can contain user input (project context,
    // tokens, PII passed through to an API client). Render them only in DEV.
    // In production the user sees the friendly copy; the real error is in
    // Sentry via componentDidCatch above.
    const message = this.state.error.message || String(this.state.error)
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-2 flex size-10 items-center justify-center rounded-full bg-destructive/10 text-destructive">
              <AlertTriangle className="size-5" aria-hidden />
            </div>
            <CardTitle className="text-xl">Something went wrong</CardTitle>
            <CardDescription>
              We hit an unexpected error before the app could load. Reloading
              the page is usually enough to recover.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {import.meta.env.DEV ? (
              <details className="rounded-md bg-muted p-3 text-left text-xs">
                <summary className="cursor-pointer text-muted-foreground">
                  Error details
                </summary>
                <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words">
                  {message}
                </pre>
              </details>
            ) : null}
            <Button className="w-full" onClick={this.handleReload}>
              Reload the page
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }
}
